import { gql } from '@/__generated__';
import {
  ParcelOrderByWithRelationInput,
  ParcelsQuery,
  ParcelStatusScalarFieldEnum,
  ParcelWhereInput,
  QueryMode,
  SortOrder,
} from '@/__generated__/graphql';
import { useQuery } from '@apollo/client';
import {
  Alert,
  Anchor,
  Button,
  Group,
  Loader,
  NumberInput,
  Paper,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDebouncedValue } from '@mantine/hooks';
import { IconX } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useMemo, useState } from 'react';

const parcelsDocument = gql(/* GraphQL */ `
  query Parcels(
    $where: ParcelWhereInput
    $orderBy: [ParcelOrderByWithRelationInput!]
    $take: Int
    $skip: Int
  ) {
    parcels(where: $where, orderBy: $orderBy, take: $take, skip: $skip) {
      id
      externalId
      trackingNumber
      createdAt
      updatedAt
      statuses {
        id
        statusCode
        statusDate
        statusDescription
        statusInfo
        depotCity
        depotNumber
      }
      shipment {
        id
        order {
          id
          package
          total
          customer {
            id
            name
          }
        }
      }
    }
  }
`);

const countParcelsDocument = gql(/* GraphQL */ `
  query CountParcels($where: ParcelWhereInput!) {
    countParcels(where: $where)
  }
`);

const parcelStatusGroupByDocument = gql(/* GraphQL */ `
  query ParcelStatusGroupBy(
    $by: [ParcelStatusScalarFieldEnum!]!
    $where: ParcelStatusWhereInput
    $orderBy: [ParcelStatusOrderByWithAggregationInput!]
  ) {
    # eslint-disable-next-line @graphql-eslint/require-selections
    parcelStatusGroupBy(by: $by, where: $where, orderBy: $orderBy) {
      statusCode
      statusDescription
      # eslint-disable-next-line @graphql-eslint/require-selections
      _count {
        statusCode
      }
    }
  }
`);

export const ParcelListPage = () => {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [filterStatus, setFilterStatus] = useState<ParcelWhereInput>({});
  const [where] = useDebouncedValue(filterStatus, 500);

  const [sortStatus, setSortStatus] = useState<
    DataTableSortStatus<ParcelsQuery['parcels'][number]>
  >({
    columnAccessor: 'createdAt',
    direction: 'desc',
  });
  const orderBy = useMemo<ParcelOrderByWithRelationInput>(() => {
    switch (sortStatus.columnAccessor) {
      default:
        return { [sortStatus.columnAccessor]: sortStatus.direction };
    }
  }, [sortStatus]);

  const createdAt =
    dateRange[0] || dateRange[1]
      ? {
          ...(dateRange[0] && { gte: dayjs(dateRange[0]).startOf('day').toDate() }),
          ...(dateRange[1] && { lte: dayjs(dateRange[1]).endOf('day').toDate() }),
        }
      : undefined;

  const { data: count } = useQuery(countParcelsDocument, {
    variables: {
      where: {
        ...where,
        createdAt,
      },
    },
  });

  const { data, loading } = useQuery(parcelsDocument, {
    variables: {
      skip: pageSize * page - pageSize,
      take: pageSize,
      orderBy,
      where: {
        ...where,
        createdAt,
      },
    },
  });

  const { data: groupedStatuses, loading: groupedStatusesLoading } = useQuery(
    parcelStatusGroupByDocument,
    {
      variables: {
        by: [ParcelStatusScalarFieldEnum.StatusCode, ParcelStatusScalarFieldEnum.StatusDescription],
        orderBy: {
          statusCode: SortOrder.Asc,
        },
        where: {
          parcel: {
            is: {
              ...(where.externalId && { externalId: where.externalId }),
              ...(where.trackingNumber && { trackingNumber: where.trackingNumber }),
              ...(where.shipment && { shipment: where.shipment }),
              ...(createdAt && { createdAt }),
            },
          },
        },
      },
    }
  );

  return (
    <Stack>
      <Paper p="md" withBorder component={Group}>
        <DatePickerInput
          placeholder="Dátum"
          type="range"
          value={dateRange}
          onChange={setDateRange}
          allowSingleDateInRange
          clearable
          valueFormat="DD.MM.YYYY"
        />
        <TextInput
          placeholder="GLS ID"
          value={filterStatus.externalId?.contains ?? ''}
          onChange={(event) => {
            setFilterStatus((prev) => ({
              ...prev,
              externalId:
                event.currentTarget.value.length > 0
                  ? { contains: event.currentTarget.value, mode: QueryMode.Insensitive }
                  : undefined,
            }));
          }}
        />
        <TextInput
          placeholder="Tracking"
          value={filterStatus.trackingNumber?.contains ?? ''}
          onChange={(event) => {
            setFilterStatus((prev) => ({
              ...prev,
              trackingNumber:
                event.currentTarget.value.length > 0
                  ? { contains: event.currentTarget.value, mode: QueryMode.Insensitive }
                  : undefined,
            }));
          }}
        />
        <TextInput
          placeholder="Zákazník"
          value={filterStatus.shipment?.is?.order?.is?.customer?.is?.name?.contains ?? ''}
          onChange={(event) =>
            setFilterStatus((prev) => ({
              ...prev,
              shipment: {
                is: {
                  order: {
                    is: {
                      customer: {
                        is: {
                          name:
                            event.currentTarget.value.length > 0
                              ? { contains: event.currentTarget.value, mode: QueryMode.Insensitive }
                              : undefined,
                        },
                      },
                    },
                  },
                },
              },
            }))
          }
        />
        <NumberInput
          placeholder="Balík č."
          value={filterStatus.shipment?.is?.order?.is?.package?.equals ?? ''}
          onChange={(value) =>
            setFilterStatus((prev) => ({
              ...prev,
              shipment: {
                is: { order: { is: { package: value !== '' ? { equals: +value } : undefined } } },
              },
            }))
          }
        />
      </Paper>
      <Paper p="md" withBorder component={Group}>
        {groupedStatusesLoading ? (
          <Alert variant="light" color="blue">
            <Loader size={16} />
            <Text inherit>Načítavám štatistiku...</Text>
          </Alert>
        ) : groupedStatuses?.parcelStatusGroupBy.length === 0 ? (
          <Alert variant="light" color="yellow">
            <Text inherit>Nie je dostupná štatistika</Text>
          </Alert>
        ) : (
          groupedStatuses?.parcelStatusGroupBy.map((group) => (
            <Button
              key={group.statusCode}
              variant="light"
              onClick={() =>
                setFilterStatus((prev) => ({
                  ...prev,
                  statuses:
                    prev.statuses?.some?.statusCode?.equals === group.statusCode
                      ? undefined
                      : { some: { statusCode: { equals: group.statusCode } } },
                }))
              }
              color={
                filterStatus.statuses?.some?.statusCode?.equals === group.statusCode
                  ? 'green'
                  : 'blue'
              }
              rightSection={
                filterStatus.statuses?.some?.statusCode?.equals === group.statusCode && (
                  <IconX size={16} />
                )
              }
            >
              {group.statusCode} - {group.statusDescription} ({group._count?.statusCode})
            </Button>
          ))
        )}
      </Paper>
      <DataTable
        withTableBorder
        striped
        highlightOnHover
        borderRadius="sm"
        minHeight={200}
        records={data?.parcels ?? []}
        fetching={loading}
        totalRecords={count?.countParcels ?? 0}
        recordsPerPage={pageSize}
        page={page}
        onPageChange={setPage}
        recordsPerPageOptions={[5, 10, 25, 50, 75, 100]}
        onRecordsPerPageChange={setPageSize}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        columns={[
          {
            accessor: 'shipment.order.package',
            title: '#',
          },
          {
            accessor: 'shipment.order.customer.name',
            title: 'Zákazník',
          },
          {
            accessor: 'shipment.order.total',
            title: 'Suma',
            render: ({ shipment }) => <Text inherit>{shipment.order.total} €</Text>,
          },
          {
            accessor: 'externalId',
            title: 'GLS ID',
            render: ({ externalId, trackingNumber }) => (
              <Stack gap={0}>
                <Text inherit>{externalId}</Text>
                <Anchor
                  inherit
                  href={`https://gls-group.eu/SK/sk/sledovanie-zasielok.html?match=${trackingNumber}`}
                  target="_blank"
                >
                  {trackingNumber}
                </Anchor>
              </Stack>
            ),
          },
          {
            accessor: 'statuses',
            title: 'Posledný stav',
            render: ({ statuses }) => {
              const lastStatus = [...(statuses ?? [])]
                .sort((a, b) => new Date(b.statusDate).getTime() - new Date(a.statusDate).getTime())
                .at(0);

              if (!lastStatus) return <Text inherit>-</Text>;

              return (
                <Text inherit>
                  {lastStatus.statusCode}-{lastStatus.statusDescription} ({statuses?.length}{' '}
                  záznamy)
                </Text>
              );
            },
          },
          {
            accessor: 'createdAt',
            title: 'Dátum',
            render: ({ createdAt, updatedAt }) => (
              <Stack gap={0}>
                <Text inherit>{dayjs(createdAt).format('DD.MM.YYYY HH:mm:ss')}</Text>
                {updatedAt > createdAt && (
                  <Text inherit c="dimmed">
                    {dayjs(updatedAt).format('DD.MM.YYYY HH:mm:ss')}
                  </Text>
                )}
              </Stack>
            ),
            sortable: true,
          },
        ]}
        rowExpansion={{
          expandable: ({ record }) => (record.statuses?.length ?? 0) > 0,
          content: ({ record }) => (
            <DataTable
              withTableBorder
              striped
              highlightOnHover
              borderRadius="sm"
              records={[...(record.statuses ?? [])].sort(
                (a, b) => new Date(b.statusDate).getTime() - new Date(a.statusDate).getTime()
              )}
              columns={[
                { accessor: 'statusCode', title: 'Kód' },
                {
                  accessor: 'statusDescription',
                  title: 'Popis',
                },
                {
                  accessor: 'statusInfo',
                  title: 'Info',
                },
                {
                  accessor: 'depotCity',
                  title: 'Depo',
                  render: ({ depotCity, depotNumber }) => (
                    <Text inherit>
                      {depotCity} ({depotNumber})
                    </Text>
                  ),
                },
                {
                  accessor: 'statusDate',
                  title: 'Dátum',
                  render: ({ statusDate }) => (
                    <Text inherit>{dayjs(statusDate).format('DD.MM.YYYY HH:mm:ss')}</Text>
                  ),
                },
              ]}
            />
          ),
        }}
      />
    </Stack>
  );
};
